/**
 * Single source of truth for outbound URLs and navigation.
 * Every page reads from here so links can never drift apart.
 */

export const WEBAPP_URL =
  process.env.REACT_APP_PABSYNC_WEBAPP_URL || "https://orders.pabs.online";

// The app has no separate registration step: the first sign-in (Google,
// Apple, or phone) creates the account, so "sign up" is the web app itself.
export const SIGNUP_URL = WEBAPP_URL;

export const ANDROID_STORE_URL = process.env.REACT_APP_ANDROID_LINK || "";
export const IOS_STORE_URL = process.env.REACT_APP_IOS_LINK || "";

/**
 * Public fare schedule (peso amounts, the home-page Pricing section, the fare
 * FAQs). Hidden while the published numbers are reconciled with what the
 * backend actually charges — flip to true to bring every spot back.
 */
export const SHOW_PRICING = false;

/**
 * Merchant plan content (Starter/Business plans, trial, commission, billing:
 * the "For merchants" nav and home section, and the merchant FAQs). Hidden
 * while PABS is not signing merchants as partners. Merchant sign-up itself
 * stays visible at /merchants. Flip to true to bring every spot back.
 */
export const SHOW_MERCHANT_PLANS = false;

export const LEGAL_LAST_UPDATED = "September 30, 2026";

export type NavChild = {
  label: string;
  href: string;
  description: string;
};

export type NavItem = {
  label: string;
  href: string;
  children?: NavChild[];
};

const HIDDEN_HREFS = [
  ...(SHOW_PRICING ? [] : ["/#pricing"]),
  ...(SHOW_MERCHANT_PLANS ? [] : ["/#merchants"]),
];

const withoutHidden = <T extends { href: string }>(items: T[]) =>
  items.filter((item) => !HIDDEN_HREFS.includes(item.href));

export const PRIMARY_NAV: NavItem[] = withoutHidden([
  {
    label: "Services",
    href: "/#services",
    children: [
      {
        label: "Foods",
        href: "/#services",
        description: "Meals and drinks from local restaurants and cafés.",
      },
      {
        label: "Pabili",
        href: "/#services",
        description: "Groceries, pharmacy items, and other shopping.",
      },
      {
        label: "Pasugo",
        href: "/#services",
        description: "Errands: have a rider buy or bring something.",
      },
      {
        label: "Angkas",
        href: "/#services",
        description: "Book a rider to take you across town.",
      },
    ],
  },
  { label: "Pricing", href: "/#pricing" },
  {
    label: "For merchants",
    href: "/#merchants",
    children: [
      {
        label: "Why PABS",
        href: "/#merchants",
        description: "Reach customers across food, grocery, and pharmacy.",
      },
      {
        label: "Plans",
        href: "/#merchants",
        description: "Start free, or unlock Business with a 30-day trial.",
      },
      {
        label: "Merchant FAQs",
        href: "/faq#merchant-join",
        description: "Onboarding, commission, and billing questions.",
      },
    ],
  },
  {
    label: "Join PABS",
    href: "/jobs",
    children: [
      {
        label: "Sell on PABS",
        href: "/merchants",
        description: "List your restaurant, grocery, or pharmacy.",
      },
      {
        label: "Become a rider",
        href: "/riders",
        description: "Deliver, run errands, and give rides.",
      },
      {
        label: "Jobs",
        href: "/jobs",
        description: "Current openings at PABS.",
      },
    ],
  },
  { label: "FAQ", href: "/faq" },
  { label: "About", href: "/about" },
]);

export const FOOTER_NAV: { title: string; links: { label: string; href: string }[] }[] = [
  {
    title: "Services",
    links: [
      { label: "Foods", href: "/#services" },
      { label: "Pabili", href: "/#services" },
      { label: "Pasugo", href: "/#services" },
      { label: "Angkas", href: "/#services" },
      { label: "Pricing", href: "/#pricing" },
      { label: "Payments", href: "/#payments" },
    ].filter((link) => !HIDDEN_HREFS.includes(link.href)),
  },
  {
    title: "Company",
    links: [
      { label: "About PABS", href: "/about" },
      { label: "For merchants", href: "/#merchants" },
      { label: "Sell on PABS", href: "/merchants" },
      { label: "Become a rider", href: "/riders" },
      { label: "Jobs", href: "/jobs" },
      { label: "Contact", href: "/contact" },
      { label: "Get the app", href: "/download" },
    ].filter((link) => !HIDDEN_HREFS.includes(link.href)),
  },
  {
    title: "Support",
    links: [
      { label: "FAQ", href: "/faq" },
      { label: "Help Center", href: WEBAPP_URL },
      { label: "Privacy requests", href: "/privacy-requests" },
      { label: "Security", href: "/security" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Statement", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "Cookies Notice", href: "/cookies" },
      { label: "Subprocessors", href: "/subprocessors" },
    ],
  },
];
